<template>
    <div class="notifications">
        <div class="notification-icon" @click="toggleNotificationsPanel">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            <span v-if="notificationCount > 0" class="notification-badge">
                {{ notificationCount > 9 ? '9+' : notificationCount }}
            </span>
        </div>

        <!-- Panneau de notifications -->
        <div v-if="showNotificationsPanel" class="notifications-panel">
            <div class="notifications-header">
                <h3>Notifications</h3>
                <button v-if="notifications.length > 0" class="mark-all-read" @click="markAllNotificationsAsRead">
                    Tout marquer comme lu
                </button>
            </div>

            <div class="notifications-body">
                <div v-if="notificationsLoading" class="notifications-loading">
                    Chargement...
                </div>

                <div v-else-if="notifications.length === 0" class="notifications-empty">
                    Aucune notification non lue
                </div>

                <div v-else class="notifications-list">
                    <NotificationItem v-for="notification in notifications" :key="notification.id"
                        :notification="notification" @mark-as-read="markNotificationAsRead"
                        @click="handleNotificationClick" />
                </div>
            </div>

            <div class="notifications-footer">
                <button class="view-all-button" @click="viewAllNotifications">
                    Voir toutes les notifications
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { notificationService } from '@/services';
import NotificationItem from '@/views/notifications/NotificationItem.vue';
import { useNotificationsStore } from '@/store/index';
import { storeToRefs } from 'pinia';

export default {
    name: 'NotificationPanel',
    components: {
        NotificationItem
    },
    setup() {
        const notificationsStore = useNotificationsStore();
        const { notificationCount } = storeToRefs(notificationsStore);

        return {
            notificationsStore,
            notificationCount
        };
    },
    data() {
        return {
            showNotificationsPanel: false,
            notifications: [],
            notificationsLoading: false,
            notificationsPage: 1,
            notificationsPerPage: 5
        };
    },
    created() {
        this.notificationsStore.loadNotificationCount();
    },
    mounted() {
        // Ajouter un écouteur d'événements pour fermer le panneau lorsqu'on clique ailleurs
        document.addEventListener('click', this.handleClickOutside);
    },
    beforeUnmount() {
        // Nettoyer l'écouteur d'événements
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        // La méthode loadNotificationCount est maintenant gérée par le store Pinia


        async loadNotifications() {
            if (this.notificationsLoading) return;

            this.notificationsLoading = true;
            try {
                const response = await notificationService.getUserNotifications({
                    page: this.notificationsPage,
                    perPage: this.notificationsPerPage,
                    unread: true
                });

                if (response.data.data) {
                    // API format paginé
                    this.notifications = response.data.data;
                } else {
                    // API format simple
                    this.notifications = response.data;
                }
            } catch (error) {
                console.error('Erreur lors du chargement des notifications:', error);
            } finally {
                this.notificationsLoading = false;
            }
        },

        toggleNotificationsPanel() {
            this.showNotificationsPanel = !this.showNotificationsPanel;

            if (this.showNotificationsPanel) {
                this.loadNotifications();
            }
        },

        async markNotificationAsRead(notification) {
            try {
                await notificationService.markAsRead(notification.id);

                // Mettre à jour localement
                const index = this.notifications.findIndex(n => n.id === notification.id);
                if (index !== -1) {
                    this.notifications[index].read_at = new Date().toISOString();
                }

                // Mettre à jour le compteur via le store Pinia
                const newCount = Math.max(0, this.notificationCount - 1);
                this.notificationsStore.updateNotificationCount(newCount);

            } catch (error) {
                console.error('Erreur lors du marquage de la notification comme lue:', error);
            }
        },

        async markAllNotificationsAsRead() {
            try {
                await notificationService.markAllAsRead();

                // Marquer toutes les notifications comme lues localement
                this.notifications.forEach(notification => {
                    notification.read_at = new Date().toISOString();
                });

                // Réinitialiser le compteur via le store Pinia
                this.notificationsStore.updateNotificationCount(0);

            } catch (error) {
                console.error('Erreur lors du marquage de toutes les notifications comme lues:', error);
            }
        },

        handleNotificationClick(notification) {
            this.showNotificationsPanel = false;

            // Rediriger selon le type de notification
            switch (notification.type) {
                case 'new_sale':
                    if (notification.data && notification.data.sale_id) {
                        this.$router.push({ name: 'sale-details', params: { id: notification.data.sale_id } });
                    } else {
                        this.$router.push({ name: 'sales' });
                    }
                    break;
                case 'low_stock':
                    this.$router.push({ name: 'products' });
                    break;
                case 'payment_received':
                    this.$router.push({ name: 'sales' });
                    break;
                case 'refund':
                    if (notification.data && notification.data.sale_id) {
                        this.$router.push({ name: 'sale-details', params: { id: notification.data.sale_id } });
                    } else {
                        this.$router.push({ name: 'sales' });
                    }
                    break;
                case 'new_customer':
                    this.$router.push({ name: 'customers' });
                    break;
                default:
                    // Par défaut, ne rien faire ou rediriger vers le tableau de bord
                    break;
            }
        },

        viewAllNotifications() {
            this.showNotificationsPanel = false;
            this.$router.push({ name: 'notifications' });
        },

        handleClickOutside(event) {
            const panel = this.$el;
            if (panel && !panel.contains(event.target) && this.showNotificationsPanel) {
                this.showNotificationsPanel = false;
            }
        }
    }
};
</script>

<style scoped>
.notifications {
    position: relative;
    cursor: pointer;
}

.notification-icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    color: #0A5C6B;
    background-color: #f5f7fa;
    transition: background-color 0.2s;
}

.notification-icon:hover {
    background-color: #e9ecf3;
}

.notification-badge {
    position: absolute;
    top: -4px;
    right: -4px;
    background-color: #3FDA4D;
    color: white;
    border-radius: 50%;
    width: 18px;
    height: 18px;
    font-size: 0.7rem;
    display: flex;
    justify-content: center;
    align-items: center;
    border: 2px solid white;
}

/* Panneau de notifications */
.notifications-panel {
    position: absolute;
    top: 48px;
    right: -100px;
    width: 320px;
    background-color: white;
    border-radius: 8px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    z-index: 1000;
    max-height: 450px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.notifications-header {
    padding: 12px 16px;
    border-bottom: 1px solid #eee;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.notifications-header h3 {
    margin: 0;
    font-size: 1rem;
    color: #2c3e50;
}

.mark-all-read {
    background: none;
    border: none;
    cursor: pointer;
    font-size: 0.8rem;
    color: #0A5C6B;
    padding: 0;
}

.mark-all-read:hover {
    text-decoration: underline;
}

.notifications-body {
    flex: 1;
    overflow-y: auto;
    max-height: 350px;
}

.notifications-loading,
.notifications-empty {
    padding: 20px;
    text-align: center;
    color: #666;
    font-size: 0.9rem;
}

.notifications-footer {
    padding: 12px 16px;
    border-top: 1px solid #eee;
    text-align: center;
}

.view-all-button {
    width: 100%;
    padding: 8px 0;
    background-color: #f5f5f5;
    border: none;
    border-radius: 4px;
    color: #2c3e50;
    font-size: 0.9rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.view-all-button:hover {
    background-color: #e0e0e0;
}

@media (max-width: 768px) {
    .notifications-panel {
        right: -40px;
        width: 290px;
    }
}
</style>